import {useAllFeePeriodsQuery} from '@transactions/graphql';
import {DateTime} from 'luxon';
import * as React from 'react';
import {CardSection} from 'src/BenefitsOverviewView/CardSection';
import {ClaimQueue} from 'src/BenefitsOverviewView/ClaimQueue';
import {
  groupFeePeriodsByCard,
} from 'src/BenefitsOverviewView/groupFeePeriodsByCard';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {
  formatCents,
  formatCentsSigned,
  formatNetShort,
} from 'src/CardBenefitsView/benefitUtils';
import {summarizeFeeYear} from 'src/CardBenefitsView/benefitWindows';
import {useNowTick} from 'src/CardBenefitsView/useNowTick';
import {StatTile} from 'src/CardBenefitsView/YearScorecard';

export const BenefitsOverviewView = () => {
  const {data, loading, refetch} = useAllFeePeriodsQuery();

  useBreadcrumb({label: 'Benefits', reset: true});
  // Queue membership and countdowns are clock-derived; tick so a page left
  // open across a day boundary stays honest
  useNowTick();

  const handleRefetch = () => {
    void refetch();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-muted-foreground">Loading benefits...</span>
      </div>
    );
  }

  // Time-dependent (window states shift with the clock), so derive per render
  const now = DateTime.now();
  const allFeePeriods = data?.allFeePeriods ?? [];
  const cardGroups = groupFeePeriodsByCard(allFeePeriods);
  const currentGroups = cardGroups.filter((group) => group.isCurrent);
  const currentPeriods = currentGroups.map((group) => group.displayPeriod);

  // Portfolio totals across every card's current cycle
  let totalFees = 0;
  let totalCredits = 0;
  let totalMissed = 0;
  let missedWindows = 0;
  for (const feePeriod of currentPeriods) {
    const stats = summarizeFeeYear(feePeriod, now);
    totalFees += stats.fee;
    totalCredits += stats.captured;
    totalMissed += stats.missed;
    missedWindows += stats.missedWindowCount;
  }
  const totalNet = totalCredits - totalFees;

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <h2 className="text-lg font-semibold">Benefits</h2>

      {cardGroups.length === 0 ? (
        <div className="border-border rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No card benefits.</p>
        </div>
      ) : (
        <>
          {currentPeriods.length > 0 && (
            <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
              <StatTile
                value={formatCents(totalFees)}
                sub={`fees · ${currentPeriods.length} active card${currentPeriods.length === 1 ? '' : 's'}`}
              />
              <StatTile
                // Signed: clawbacks can push the aggregate negative, and an
                // unsigned total would contradict the net tile beside it
                value={formatCentsSigned(totalCredits)}
                sub="credits, posted + booked, current cycles"
              />
              <StatTile
                value={`net ${formatNetShort(totalNet)}`}
                tone={totalNet >= 0 ? 'sage' : 'red'}
                sub="all current cycles"
              />
              <StatTile
                value={totalMissed > 0 ? formatCents(totalMissed) : 'nothing'}
                tone={totalMissed > 0 ? 'amber' : 'default'}
                sub={
                  totalMissed > 0
                    ? `missed · ${missedWindows} expired window${missedWindows === 1 ? '' : 's'}`
                    : 'missed so far'
                }
              />
            </div>
          )}

          {/* Every fee period, not just current cycles: calendar-aligned
              schedules snap outward, so a window can open days before its
              own fee period starts (and a lapsed card can still have an
              open tail). buildClaimQueue keeps only open windows. */}
          <ClaimQueue feePeriods={allFeePeriods} onUpdate={handleRefetch} />

          <div>
            <h3 className="text-muted-foreground mb-1.5 px-0.5 text-[11px] font-semibold tracking-wider uppercase">
              Cards
            </h3>
            <div className="border-border bg-card divide-border divide-y rounded-lg border">
              {cardGroups.map((group) => (
                <CardSection key={group.accountId} group={group} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
